import { useCallback, useMemo } from 'react';

import {
  type ConsumerCheckoutCartsResponse,
  useShowConsumerCheckout,
  useUpdateConsumerCheckout,
} from '@givebacks/api/store';
import { useFormyValidator } from '@givebacks/formy';
import { keepPreviousData } from '@tanstack/react-query';
import {
  CheckoutInfo,
  CheckoutInfoProvider,
  STRIPE_TO_TOKENEX_CARD_TYPE_MAP,
  TippingForm,
} from 'core/Components';
import { Card } from 'ui';

import { CheckoutForms } from '../checkout.types';
import { useCausesQueries } from '../hooks/useCausesQueries';
import classes from './CheckoutFees.module.css';
import { CheckoutProcessingFeesForm } from './CheckoutProcessingFeesForm';

type CheckoutFeesProps = {
  checkoutId: string;
};

const CheckoutTipForm = ({
  checkoutResponse,
  cartIds,
}: {
  checkoutResponse: ConsumerCheckoutCartsResponse;
  cartIds: string[];
}) => {
  const { mutate: updateCheckoutTip } = useUpdateConsumerCheckout();

  const causeIds = useMemo(() => {
    return (
      checkoutResponse?.checkout.carts
        .filter(({ ask_for_tip }) => Boolean(ask_for_tip))
        .map(({ cause_id }) => cause_id) ?? []
    );
  }, [checkoutResponse?.checkout.carts]);

  const { causesNameString } = useCausesQueries(causeIds);

  const handleTipChange = useCallback(
    ({ tipAmount, asPercent }: { tipAmount: number; asPercent: boolean }) => {
      if (!cartIds.length) return;

      const percentAmount = asPercent ? tipAmount : null;
      const tipCents = asPercent ? null : tipAmount;
      // NOTE: We need to send both percent and cents to the API because the
      // API needs to know which one to use, and which one to nil out.
      const tipRequest = {
        tip_percent: percentAmount,
        tip_cents: tipCents,
      };

      updateCheckoutTip({
        checkout: tipRequest,
        checkoutId: checkoutResponse?.checkout.uuid,
        cartIds: cartIds,
      });
    },
    [cartIds, checkoutResponse?.checkout.uuid, updateCheckoutTip]
  );
  return (
    <TippingForm
      causeNames={causesNameString}
      total={checkoutResponse?.checkout.subtotal ?? 0}
      isPledge={checkoutResponse?.checkout.pledge_required}
      onChange={handleTipChange}
    />
  );
};

const CheckoutTipFormGuard = ({
  checkoutResponse,
  cartIds,
}: {
  checkoutResponse: ConsumerCheckoutCartsResponse;
  cartIds: string[];
}) => {
  if (checkoutResponse.checkout.ask_for_tip == false) return null;
  return (
    <Card.Section className={classes.CheckoutFees_section} inheritPadding>
      <CheckoutTipForm checkoutResponse={checkoutResponse} cartIds={cartIds} />
    </Card.Section>
  );
};

const CheckoutCartInfo = ({
  checkoutResponse,
}: {
  checkoutResponse: ConsumerCheckoutCartsResponse;
}) => {
  return (
    <Card.Section inheritPadding className={classes.CheckoutFees_section}>
      <CheckoutInfoProvider value={{ checkoutResponse }}>
        <CheckoutInfo />
      </CheckoutInfoProvider>
    </Card.Section>
  );
};

const CheckoutFees = ({ checkoutId }: CheckoutFeesProps) => {
  const { useGetForm } = useFormyValidator<CheckoutForms>();

  const stripeCardForm = useGetForm('stripe_credit_card');
  const existingCardForm = useGetForm('existing_credentials');
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const pendingCredential = useMemo(() => {
    if (existingCardForm?.values?.card_type) {
      return existingCardForm?.values?.card_type;
    } else if (stripeCardForm?.values?.card_type) {
      return STRIPE_TO_TOKENEX_CARD_TYPE_MAP[stripeCardForm?.values?.card_type];
    }
    return undefined;
  }, [stripeCardForm?.values?.card_type, existingCardForm?.values?.card_type]);

  const { data: checkoutResponse } = useShowConsumerCheckout({
    checkoutId,
    pending_card_type: pendingCredential,
    placeholderData: keepPreviousData,
  });

  const cartIds = useMemo(() => {
    return checkoutResponse?.checkout.carts.map(({ uuid }) => uuid) ?? [];
  }, [checkoutResponse]);

  if (!checkoutResponse || !cartIds.length) return null;

  return (
    <Card withBorder radius="sm" className={classes.CheckoutFees_card} p="md">
      <CheckoutProcessingFeesForm checkoutResponse={checkoutResponse} cartIds={cartIds} />

      <CheckoutTipFormGuard checkoutResponse={checkoutResponse} cartIds={cartIds} />

      <CheckoutCartInfo checkoutResponse={checkoutResponse} />
    </Card>
  );
};

export { CheckoutFees };
